<template>
    <div class="page-dobrovolnici bg-base">
        <div class="container space-y-8">
            <!-- Hero Header Section -->
            <section class="relative">
                <div class="mx-auto max-w-4xl py-8 text-center md:py-12">
                    <!-- Title: Match blog style (text-5xl) -->
                    <h1 class="mb-4 text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl md:text-5xl">
                        Dobrovoľníci pripravení pomáhať
                    </h1>
                    <!-- Description: Match blog style (text-xl) -->
                    <p class="mx-auto mb-8 max-w-2xl text-xl text-gray-600 dark:text-gray-300">
                        Nájdi dobrovoľníkov pre svoju organizáciu alebo projekt. Spoj sa s ľuďmi, ktorí chcú pomáhať.
                    </p>

                    <!-- Action Buttons -->
                    <ClientOnly>
                        <div v-if="!hasPublishedVolunteerProfile && applicationStore.isLoggedIn" class="flex flex-wrap justify-center gap-4 mb-8">
                            <NuxtLink
                                :to="{ name: 'profile-volunteer-preferences' }"
                                class="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
                            >
                                <Icon name="heroicons:user-plus" class="size-5" />
                                Vytvor dobrovoľnícky profil
                            </NuxtLink>
                        </div>
                    </ClientOnly>

                    <!-- Filter Section -->
                    <div class="mx-auto max-w-3xl text-left">
                        <VolunteersFilter :model-value="filters" @update:model-value="updateFilters">
                            <div class="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 sm:flex sm:w-auto sm:justify-start">
                                <FilterSortTabs
                                    :model-value="sortMode"
                                    :options="volunteerSortOptions"
                                    @update:model-value="setSortMode"
                                />
                                <CViewToggle v-model="viewMode" />
                            </div>
                        </VolunteersFilter>
                    </div>
                </div>
            </section>

            <!-- Loading indicator -->
            <CardRowLoadingIndicator v-if="isSearching" />

            <!-- Volunteers Grid/List -->
            <div v-else-if="hasVolunteerResults" class=" pb-12">
                <div class="mb-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                    {{ searchResults.nbHits }} výsledkov
                </div>

                <!-- Cards View (Masonry) -->
                <CMasonryGrid
                    v-if="viewMode === 'cards'"
                    :items="displayedVolunteers"
                    :column-width="280"
                    :gap="24"
                    :ssr-columns="3"
                    mode="masonry"
                >
                    <template #default="slotProps">
                        <CMasonryCard
                            :data="getMasonryVolunteer(slotProps)"
                            type="volunteer"
                        />
                    </template>
                </CMasonryGrid>

                <!-- Rows View (List) -->
                <div v-else class="space-y-3">
                    <VolunteerRow
                        v-for="volunteer in displayedVolunteers"
                        :key="volunteer.id || volunteer.objectID"
                        :volunteer="volunteer"
                    />
                </div>

                <!-- Pagination Controls -->
                <div class="mt-8">
                    <CLineBreak class="mb-4" dark-color />
                    <div class="grid grid-cols-3 items-center">
                        <div class="order-1 col-span-3 mb-4 text-center md:order-2 md:col-span-1 md:mb-0">
                            <CButton
                                v-if="hasNextPage"
                                class="w-full md:w-auto"
                                variant="primary"
                                @click="loadNextPage"
                            >
                                Zobraziť viac ({{ numberOfItemsOnNextPage }})
                            </CButton>
                        </div>
                        <div class="order-2 col-span-1 md:order-1">
                            <CButton icon="arrow-top" variant="primary-link" @click="scrollToTop">
                                Hore
                            </CButton>
                        </div>
                        <div class="order-3 col-span-2 text-right md:col-span-1">
                            <Paging
                                :pages="searchResults.nbPages || 0"
                                :value="page"
                                @input="changePage"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- No results -->
            <NothingFound v-else :search-term="textSearch || 'ale žiadneho dobrovoľníka'" />

            <ListingSeoLinks class="pb-4" title="Ako začať a kam ďalej" :links="volunteersListingLinks" />
        </div>
    </div>
</template>

<script setup lang="ts">
import Paging from '@/components/dashboard/Paging.vue'
import CViewToggle from '@/components/ui/CViewToggle.vue'
import type { ViewMode } from '@/components/ui/CViewToggle.vue'
import FilterSortTabs from '@/components/filters/components/FilterSortTabs.vue'
import VolunteerRow from '@/components/volunteer/VolunteerRow.vue'
import ListingSeoLinks from '@/components/common/ListingSeoLinks.vue'
import { volunteersListingLinks } from '@/constants/listingSeoLinks'

import type { PagedSearchResults } from '#shared/types/search'
import type { UserProfile } from '#shared/types/User'
import { VisibilityType } from '#shared/types/visibility'
import { useTypesense } from '@/composables/typesense/useTypesense'
import { useExponea } from '@/composables/exponea/useExponea'
import { useVolunteers } from '@/composables/volunteers/useVolunteers'
import { parseFiltersFromQuery, serializeFiltersIntoQuery } from '@/utils/query'

const singleValueKeys = ['q']
const multiValueKeys = ['interests', 'who', 'capacity', 'cooperation', 'region', 'experience']
type VolunteerSearchHit = UserProfile & { objectID?: string }

const mapFiltersFromRouteQueryToSearchFields = (queryFilters: any): Record<string, any> => {
    return {
        volunteerInterests: queryFilters.interests,
        volunteerTimeCapacity: queryFilters.capacity,
        volunteerHelpWho: queryFilters.who,
        volunteerCooperation: queryFilters.cooperation,
        volunteerGeoRegion: queryFilters.region,
        volunteerPrefersFieldOfExperience: queryFilters.experience
    }
}

const route = useRoute()
const router = useRouter()
const applicationStore = useApplicationStore()
const { serializeFilters, joinSearchResults } = useTypesense()
const { searchVolunteers } = useVolunteers()
const { trackExponeaEvent } = useExponea()

type SortMode = 'recommended' | 'newest'

const volunteerSortOptions = [
    { value: 'recommended', label: 'Odporúčané', shortLabel: 'Tip' },
    { value: 'newest', label: 'Najnovšie', shortLabel: 'Nové' }
]
const defaultSortMode: SortMode = 'recommended'

const isSearching = ref(true)
const searchResults = ref<PagedSearchResults<VolunteerSearchHit> | undefined>(undefined)

// View mode (cards or rows)
const viewMode = ref<ViewMode>('cards')

// Head meta — the page shipped without any <title> or description before this
useHeadMeta()

const hasPublishedVolunteerProfile = computed(() => {
    return applicationStore.userProfile?.volunteerProfile?.visibility === VisibilityType.published
})

const textSearch = computed(() => {
    return (route.query.q || '') as string
})

const sortMode = computed<SortMode>(() => {
    const requestedSort = route.query.sort
    if (requestedSort === 'recommended' || requestedSort === 'newest') {
        return requestedSort
    }
    return defaultSortMode
})

const page = computed(() => {
    return Number(route.query.page) || 1
})

const filters = computed(() => {
    return parseFiltersFromQuery(route.query, {
        singleValueKeys,
        multiValueKeys
    })
})

const hasNextPage = computed(() => {
    if (!searchResults.value) return false
    return page.value < (searchResults.value.nbPages || 0)
})

const pagedVolunteerHits = computed<Record<number, VolunteerSearchHit[]>>(() => {
    const pagedHits = searchResults.value?.pagedHits || {
        [searchResults.value?.page || 0]: searchResults.value?.hits || []
    }
    const seenIds = new Set<string>()
    const dedupedPages: Record<number, VolunteerSearchHit[]> = {}

    Object.entries(pagedHits)
        .sort(([pageA], [pageB]) => Number(pageA) - Number(pageB))
        .forEach(([pageNumber, hits]) => {
            dedupedPages[Number(pageNumber)] = hits.filter((volunteer) => {
                const key = volunteer.id || volunteer.objectID
                if (!key) return true
                if (seenIds.has(key)) return false

                seenIds.add(key)
                return true
            })
        })

    return dedupedPages
})

const displayedVolunteers = computed(() => {
    return Object.values(pagedVolunteerHits.value).flat()
})

const hasVolunteerResults = computed(() => {
    return displayedVolunteers.value.length > 0
})

const numberOfItemsOnNextPage = computed(() => {
    if (!searchResults.value) return 0
    const totalRemainingItems = (searchResults.value.nbHits || 0) - page.value * (searchResults.value.hitsPerPage || 0)
    return Math.min(totalRemainingItems, searchResults.value.hitsPerPage || 0)
})

watch(
    () => route.query,
    async (newQuery, oldQuery) => {
        const pageChanged = newQuery.page !== oldQuery?.page
        const filtersChanged = JSON.stringify({ ...newQuery, page: undefined }) !== JSON.stringify({ ...oldQuery, page: undefined })

        if (filtersChanged) {
            searchResults.value = undefined
        }

        if (filtersChanged || pageChanged) {
            await searchVolunteersAction()
        }
    },
    { deep: true }
)

onMounted(() => {
    searchVolunteersAction()
    scrollToCardFromSession()
})

function changePage(newPage: number) {
    router.push({
        query: {
            ...route.query,
            page: newPage > 1 ? String(newPage) : undefined
        }
    })

    trackExponeaEvent({
        eventName: 'volunteer_search',
        userId: applicationStore.userId,
        eventProperties: { action: 'change_page', page: newPage }
    })
}

function loadNextPage() {
    if (hasNextPage.value) {
        changePage(page.value + 1)
    }
}

function scrollToTop() {
    if (import.meta.client) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }
}

function updateFilters(newFilters: any) {
    const query = serializeFiltersIntoQuery(newFilters, {
        singleValueKeys,
        multiValueKeys
    })

    router.push({
        name: route.name!,
        query: {
            ...query,
            sort: sortMode.value === defaultSortMode ? undefined : sortMode.value,
            page: undefined
        }
    })
}

function setSortMode(mode: string) {
    const nextMode = mode as SortMode
    if (nextMode === sortMode.value) return

    router.push({
        name: route.name!,
        query: {
            ...route.query,
            sort: nextMode === defaultSortMode ? undefined : nextMode,
            page: undefined
        }
    })
}

function getMasonryVolunteer(slotProps: unknown): VolunteerSearchHit {
    return (slotProps as { item: VolunteerSearchHit }).item
}

async function searchVolunteersAction() {
    const filterValues = mapFiltersFromRouteQueryToSearchFields(filters.value)
    const sortBy = sortMode.value === 'newest' ? 'createdAt:desc' : undefined

    isSearching.value = true

    const results = await searchVolunteers(textSearch.value, {
        page: page.value - 1,
        filters: serializeFilters(filterValues),
        sort_by: sortBy,
        requireProfileImg: page.value === 1
    })

    // Use joinSearchResults to accumulate pages (same pattern as praca/mentori pages)
    searchResults.value = joinSearchResults(results, searchResults.value)

    isSearching.value = false
}
</script>
